import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import './authPage.css'
import homeIcon from '../assets/icon/homeIcon.png'
import starIcon from '../assets/icon/starIcon.png'
import { login } from '../functions/login'
import { signUp } from '../functions/signup'
import { sendVerificationCode, verifyEmailCode } from '../functions/emailVerification'
import { setToken, setUsername } from '../utils/auth'
import { useLanguage } from '../contexts/LanguageContext'


type PolicyType = 'terms' | 'privacy'

const policyContent: Record<PolicyType, {
    title: string
    subtitle: string
    sections: { heading: string; items: string[] }[]
}> = {
    terms: {
        title: 'TERMS OF SERVICE',
        subtitle: 'Spread Overview',
        sections: [
            {
                heading: '1. Acceptance Of Terms',
                items: [
                    'By creating an account, accessing the website, activating physical cards, or using any divination-related service provided by Dmat, you agree to comply with these Terms and all applicable laws.',
                    'If you do not agree to these Terms, you may not use the service.'
                ]
            },
            {
                heading: '2. Provided Services',
                items: [
                    'Online card drawing and divination interpretation.',
                    'Physical card activation and device pairing.',
                    'Membership plans and digital content.',
                    'Personal insights based on your selections and interactions (for entertainment and personal insight purposes only).'
                ]
            },
            {
                heading: '3. Eligibility',
                items: [
                    'You must be at least 13 years old (the age of digital consent in your region).',
                    'Legal adulthood is required to use online services in your location. If you are under the required age, you may use Dmat only with parental consent.'
                ]
            },
            {
                heading: '4. User Accounts & Security',
                items: [
                    'You are responsible for maintaining the confidentiality of your login credentials.',
                    'All activities under your account are your responsibility. Notify us immediately of any unauthorized use.'
                ]
            },
            {
                heading: '5. Prohibited Uses',
                items: [
                    'Reverse-engineering, reselling, or duplicating any part of the service.',
                    'Submitting content that is unlawful, harmful, or infringes on intellectual property rights.',
                    'Attempting to interfere with servers, networks, or security features.'
                ]
            },
            {
                heading: '6. Subscription & Payments',
                items: [
                    'Digital coins and memberships are non-transferable and non-refundable except where required by law.',
                    'Promotions or redeem codes may have separate terms that apply in addition to these Terms.'
                ]
            },
            {
                heading: '7. Disclaimers',
                items: [
                    'Dmat does not provide life advice, medical diagnosis, financial consulting, or legal guidance.',
                    'All readings are for entertainment and personal reflection only.'
                ]
            }
        ]
    },
    privacy: {
        title: 'PRIVACY POLICY',
        subtitle: 'Spread Overview',
        sections: [
            {
                heading: '1. Information We Collect',
                items: [
                    'Personal Information: email address, username or nickname, device pairing information (for physical card devices), membership status.',
                    'Usage Data: card draws and reading history, spread selections, interaction logs, language and region.',
                    'Device Information: browser type, operating system, IP address (anonymized where possible).'
                ]
            },
            {
                heading: '2. Optional Information You Choose To Provide',
                items: [
                    'Typical questions, topics you want to explore, voice input processed but not stored long-term.'
                ]
            },
            {
                heading: '3. How We Use Information',
                items: [
                    'Provide personalized readings, maintain your account, and sync your history across devices.',
                    'Improve services, prevent fraud, and ensure platform security.',
                    'Send service messages, important updates, or promotional content (with opt-out options).'
                ]
            },
            {
                heading: '4. Data Retention & Security',
                items: [
                    'We store information only as long as needed for the purposes described or as required by law.',
                    'Industry-standard encryption and access controls are used to safeguard stored information.'
                ]
            },
            {
                heading: '5. Your Choices',
                items: [
                    'You may request access, correction, or deletion of your personal data.',
                    'You can withdraw consent for optional data at any time in your profile settings.'
                ]
            },
            {
                heading: '6. Contact',
                items: [
                    'For privacy questions, contact support@dmat.app. We respond within 30 days where legally required.'
                ]
            }
        ]
    }
}

const AuthPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useLanguage();
    const [formType, setFormType] = useState<'signIn' | 'signUp' | 'verify'>('signIn');
    const [fullName, setFullName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [agreeToTerms, setAgreeToTerms] = useState<boolean>(true);
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [verificationCode, setVerificationCode] = useState<string>('');
    const [birthday, setBirthday] = useState<string>('');
    const [birthplace, setBirthplace] = useState<string>('');
    const [birthtime, setBirthtime] = useState<string>('');
    const [activePolicy, setActivePolicy] = useState<PolicyType | null>(null);

    const [emailError, setEmailError] = useState<string>('');
    const [passwordError, setPasswordError] = useState<string>('');
    const [confirmPasswordError, setConfirmPasswordError] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Email verification states
    const [emailCode, setEmailCode] = useState<string>('');
    const [emailCodeSent, setEmailCodeSent] = useState<boolean>(false);
    const [emailCodeError, setEmailCodeError] = useState<string>('');
    const [sendingCode, setSendingCode] = useState<boolean>(false);
    const [countdown, setCountdown] = useState<number>(0);

    const validateEmail = (email: string): boolean => {
        setEmailError('');
        if (!email) {
            setEmailError(t.authEmailRequired);
            return false;
        } else if (!email.includes('@')) {
            setEmailError(t.authInvalidEmail);
            return false;
        }
        return true;
    }

    const validatePassword = (password: string): boolean => {
        setPasswordError('');
        if (!password) {
            setPasswordError(t.authPasswordRequired);
            return false;
        } else if (password.length < 6) {
            setPasswordError(t.authPasswordTooShort);
            return false;
        }
        return true;
    }

    const validateConfirmPassword = (confirmPassword: string): boolean => {
        setConfirmPasswordError('');
        if (!confirmPassword) {
            setConfirmPasswordError(t.authConfirmPasswordRequired);
            return false;
        } else if (confirmPassword !== password) {
            setConfirmPasswordError(t.authPasswordsNotMatch);
            return false;
        }
        return true;
    }

    const clearErrors = () => {
        setErrorMessage('');
        setEmailError('');
        setPasswordError('');
        setConfirmPasswordError('');
        setEmailCodeError('');
    }

    // Countdown timer for resend code
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    // Handle send verification code
    const handleSendCode = async () => {
        if (!validateEmail(email)) return;
        if (countdown > 0) return; // 防止重复点击

        // 立即开始倒计时，防止用户频繁点击
        setCountdown(60);
        setSendingCode(true);

        try {
            const result = await sendVerificationCode(email);

            if (result.success) {
                setEmailCodeSent(true);
                // 发送成功，倒计时已经在运行
            } else {
                setEmailError(result.message);
                // 发送失败也保持倒计时，防止频繁重试
            }
        } catch (error: any) {
            setEmailError(error.message || 'Failed to send verification code');
            // 发送失败也保持倒计时，防止频繁重试
        } finally {
            setSendingCode(false);
        }
    };

    useEffect(() => {
        document.body.style.overflow = activePolicy ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        }
    }, [activePolicy]);

    // 格式化出生时间为 HH:mm:ss 格式
    const formatBirthTime = (time: string): string | undefined => {
        if (!time) return undefined;
        // 如果只有 HH:mm，添加 :00
        if (time.length === 5 && time.includes(':')) {
            return time + ':00';
        }
        return time;
    }

    const handleSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        clearErrors();

        // 验证输入（登录支持用户名或邮箱，所以只验证是否填写）
        if (!email.trim()) {
            setEmailError(t.authUsernameOrEmailRequired);
            return;
        }
        if (!validatePassword(password)) {
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        try {
            // 调用登录 API（支持用户名或邮箱登录）
            const { token } = await login(email.trim(), password);

            // 保存 token 和用户名
            setToken(token);
            setUsername(email.trim()); // 保存用户名或邮箱

            // Priority:
            // 1. location.state?.from (Standard React Router flow)
            // 2. localStorage 'redirectAfterLogin' (Fallback for persistence)

            const stateFromLocation = location.state?.from;
            const redirectPath = stateFromLocation || localStorage.getItem('redirectAfterLogin') || '/';

            // Clean up localStorage if used
            if (localStorage.getItem('redirectAfterLogin')) {
                localStorage.removeItem('redirectAfterLogin');
            }

            // Restore state if available (from location or localStorage)
            let navigationState = {};
            if (location.state) {
                // Remove 'from' to avoid polluting state of next page
                const { from, ...restSource } = location.state;
                navigationState = restSource;
            } else if (localStorage.getItem('nfcCardState')) {
                try {
                    navigationState = JSON.parse(localStorage.getItem('nfcCardState') || '{}');
                    localStorage.removeItem('nfcCardState');
                } catch (e) {
                    console.error('Error parsing persisted state', e);
                }
            }

            navigate(redirectPath, { state: navigationState, replace: true });
        } catch (error: any) {
            console.error('登录失败:', error);
            // API 函数已经处理了错误信息，直接显示
            setErrorMessage(error.message || t.authLoginFailed);
        } finally {
            setIsLoading(false);
        }
    }

    const handleSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        clearErrors();

        // 验证输入
        if (!fullName.trim()) {
            setErrorMessage(t.authFullNameRequired);
            return;
        }

        if (!validateEmail(email) || !validatePassword(password)) {
            return;
        }

        if (!validateConfirmPassword(confirmPassword)) {
            return;
        }

        // 验证邮箱验证码
        if (!emailCode.trim()) {
            setEmailCodeError('Please enter the verification code');
            return;
        }
        if (emailCode.trim().length !== 6) {
            setEmailCodeError('Verification code must be 6 digits');
            return;
        }

        if (!agreeToTerms) {
            setErrorMessage(t.authAgreeTermsRequired);
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        try {
            // 1. 先验证邮箱验证码
            const verifyResult = await verifyEmailCode(email, emailCode.trim());
            if (!verifyResult.success) {
                setEmailCodeError(verifyResult.message);
                setIsLoading(false);
                return;
            }

            // 2. 验证码正确，调用注册 API
            // 使用 fullName 作为 username，email 作为邮箱，包含验证码
            await signUp({
                username: fullName.trim(),
                password: password,
                email: email,
                emailCode: emailCode.trim(),
                birthday: birthday.trim() || undefined,
                birthplace: birthplace.trim() || undefined,
                birthtime: formatBirthTime(birthtime.trim()),
            });

            // 注册成功，显示成功消息并切换到登录页面
            setErrorMessage('');
            alert(t.authSignUpSuccess);
            setFormType('signIn');
            // 清空表单
            setFullName('');
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setEmailCode('');
            setBirthday('');
            setBirthplace('');
            setBirthtime('');
        } catch (error: any) {
            console.error('注册失败:', error);
            setErrorMessage(error.message || t.authSignUpFailed);
        } finally {
            setIsLoading(false);
        }
    }

    const handleVerify = (e: React.FormEvent) => {
        e.preventDefault();
        // console.log(verificationCode);
        // Navigate to homepage after successful verification
        navigate('/homepage');
    }

    const handleSocialLogin = (provider: string) => {
        // console.log(`Login with ${provider}`);
        // Navigate to homepage after successful social login
        navigate('/homepage');
    }

    const handleBack = () => {
        navigate(-1);
    }

    return (
        <div className="auth-page">
            <div className="auth-header-icons">
                <button className="auth-icon-btn" onClick={handleBack} aria-label="Back">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
            </div>
            <div className="auth-container">

                <div className="auth-content">
                    {formType === 'signIn' ? (
                        <>
                            <h1 className="auth-main-title">{t.authOnlineDivination}</h1>
                            <p className="auth-subtitle">{t.authWelcomeBack}</p>

                            <form className="auth-form" onSubmit={handleSignIn}>
                                {errorMessage && (
                                    <div className="auth-error-message" style={{
                                        color: '#ff4444',
                                        marginBottom: '10px',
                                        fontSize: '14px',
                                        textAlign: 'center'
                                    }}>
                                        {errorMessage}
                                    </div>
                                )}
                                <div className="form-group">
                                    <label className="form-label">{t.authEmailLabel}</label>
                                    <input
                                        type="text"
                                        className={`form-input ${emailError ? 'error' : ''}`}
                                        placeholder={t.authEmailPlaceholder}
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (emailError) setEmailError('');
                                            if (errorMessage) setErrorMessage('');
                                        }}
                                    />
                                    {emailError && (
                                        <span style={{ color: '#ff4444', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                                            {emailError}
                                        </span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t.authPasswordLabel}</label>
                                    <div className="password-input-wrapper">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            className={`form-input password-input ${passwordError ? 'error' : ''}`}
                                            placeholder={t.authPasswordPlaceholder}
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                if (passwordError) setPasswordError('');
                                                if (errorMessage) setErrorMessage('');
                                            }}
                                            onBlur={() => validatePassword(password)}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                {showPassword ? (
                                                    <>
                                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                                    </>
                                                ) : (
                                                    <>
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </>
                                                )}
                                            </svg>
                                        </button>
                                    </div>
                                    {passwordError && (
                                        <span style={{ color: '#ff4444', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                                            {passwordError}
                                        </span>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="auth-submit-btn"
                                    disabled={isLoading}
                                >
                                    {isLoading ? t.authLoggingIn : t.authLogIn}
                                </button>



                                <div className="auth-social-buttons">
                                    <button
                                        type="button"
                                        className="auth-social-btn google-btn"
                                        onClick={() => handleSocialLogin('google')}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="currentColor" />
                                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="currentColor" />
                                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="currentColor" />
                                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="currentColor" />
                                        </svg>
                                        <span>{t.authGoogle}</span>
                                    </button>

                                    <button
                                        type="button"
                                        className="auth-social-btn apple-btn"
                                        onClick={() => handleSocialLogin('apple')}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                                        </svg>
                                        <span>{t.authAppleId}</span>
                                    </button>
                                </div>
                            </form>
                            <div className="auth-divider">
                                <div className="divider-line"></div>
                                <img src={starIcon} alt="Star" className="divider-star" />
                                <div className="divider-line"></div>
                            </div>
                            <p className="auth-footer-link">
                                {t.authNoAccount} <button className="auth-link" onClick={() => setFormType('signUp')}>{t.authRegisterNow}</button>
                            </p>
                        </>
                    ) : formType === 'signUp' ? (
                        <>
                            <h1 className="auth-main-title">{t.authCreateAccount}</h1>
                            <p className="auth-subtitle">{t.authWelcome}</p>

                            <form className="auth-form" onSubmit={handleSignUp}>
                                {errorMessage && (
                                    <div className="auth-error-message" style={{
                                        color: '#ff4444',
                                        marginBottom: '10px',
                                        fontSize: '14px',
                                        textAlign: 'center'
                                    }}>
                                        {errorMessage}
                                    </div>
                                )}
                                <div className="form-group">
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder={t.authFullNamePlaceholder}
                                        value={fullName}
                                        onChange={(e) => {
                                            setFullName(e.target.value);
                                            if (errorMessage) setErrorMessage('');
                                        }}
                                    />
                                </div>
                                <div className="form-group">
                                    <div className="email-input-wrapper">
                                        <input
                                            type="email"
                                            className={`form-input email-input ${emailError ? 'error' : ''}`}
                                            placeholder={t.authEmailLabel}
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                                if (emailError) setEmailError('');
                                                if (errorMessage) setErrorMessage('');
                                            }}
                                            onBlur={() => validateEmail(email)}
                                        />
                                        <button
                                            type="button"
                                            className="send-code-btn"
                                            onClick={handleSendCode}
                                            disabled={sendingCode || countdown > 0 || !email.includes('@')}
                                        >
                                            {countdown > 0 ? `${countdown}s` : sendingCode ? 'Sending...' : 'Send Code'}
                                        </button>
                                    </div>
                                    {emailError && (
                                        <span style={{ color: '#ff4444', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                                            {emailError}
                                        </span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        className={`form-input ${emailCodeError ? 'error' : ''}`}
                                        placeholder="Verification Code"
                                        value={emailCode}
                                        onChange={(e) => {
                                            setEmailCode(e.target.value);
                                            if (emailCodeError) setEmailCodeError('');
                                        }}
                                        maxLength={6}
                                    />
                                    {emailCodeError && (
                                        <span style={{ color: '#ff4444', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                                            {emailCodeError}
                                        </span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <div className="password-input-wrapper">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            className={`form-input password-input ${passwordError ? 'error' : ''}`}
                                            placeholder={t.authPasswordLabel}
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                if (passwordError) setPasswordError('');
                                                if (errorMessage) setErrorMessage('');
                                            }}
                                            onBlur={() => validatePassword(password)}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                {showPassword ? (
                                                    <>
                                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                                    </>
                                                ) : (
                                                    <>
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </>
                                                )}
                                            </svg>
                                        </button>
                                    </div>
                                    {passwordError && (
                                        <span style={{ color: '#ff4444', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                                            {passwordError}
                                        </span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <div className="password-input-wrapper">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            className={`form-input password-input ${confirmPasswordError ? 'error' : ''}`}
                                            placeholder={t.authConfirmPasswordPlaceholder}
                                            value={confirmPassword}
                                            onChange={(e) => {
                                                setConfirmPassword(e.target.value);
                                                if (confirmPasswordError) setConfirmPasswordError('');
                                                if (errorMessage) setErrorMessage('');
                                            }}
                                            onBlur={() => validateConfirmPassword(confirmPassword)}
                                        />
                                    </div>
                                    {confirmPasswordError && (
                                        <span style={{ color: '#ff4444', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                                            {confirmPasswordError}
                                        </span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">{t.authBirthdayLabel}</label>
                                    <input
                                        type="date"
                                        className="form-input"
                                        placeholder="YYYY-MM-DD"
                                        value={birthday}
                                        min="1900-01-01"
                                        max="2099-12-31"
                                        onChange={(e) => {
                                            setBirthday(e.target.value);
                                            if (errorMessage) setErrorMessage('');
                                        }}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">{t.authBirthplaceLabel}</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder={t.authBirthplacePlaceholder}
                                        value={birthplace}
                                        onChange={(e) => {
                                            setBirthplace(e.target.value);
                                            if (errorMessage) setErrorMessage('');
                                        }}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">{t.authBirthTimeLabel}</label>
                                    <input
                                        type="time"
                                        className="form-input"
                                        placeholder="HH:mm:ss"
                                        value={birthtime}
                                        onChange={(e) => {
                                            setBirthtime(e.target.value);
                                            if (errorMessage) setErrorMessage('');
                                        }}
                                    />
                                </div>

                                <div className="form-checkbox-group">
                                    <input
                                        type="checkbox"
                                        id="agreeTerms"
                                        className="auth-checkbox"
                                        checked={agreeToTerms}
                                        onChange={(e) => setAgreeToTerms(e.target.checked)}
                                    />
                                    <label htmlFor="agreeTerms" className="auth-checkbox-label">
                                        {t.authAgreeTerms} <button type="button" className="auth-link-inline" onClick={() => setActivePolicy('terms')}>{t.authTermsOfService}</button> {t.authAnd} <button type="button" className="auth-link-inline" onClick={() => setActivePolicy('privacy')}>{t.authPrivacyPolicy}</button>
                                    </label>
                                </div>

                                <button
                                    type="submit"
                                    className="auth-submit-btn"
                                    disabled={isLoading}
                                >
                                    {isLoading ? t.authSigningUp : t.authSignUp}
                                </button>
                            </form>

                            <div className="auth-divider">
                                <div className="divider-line"></div>
                                <img src={starIcon} alt="Star" className="divider-star" />
                                <div className="divider-line"></div>
                            </div>

                            <p className="auth-or-text">{t.authOrSignUpWith}</p>

                            <div className="auth-social-buttons-circle">
                                <button
                                    type="button"
                                    className="auth-social-btn-circle"
                                    onClick={() => handleSocialLogin('google')}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="currentColor" />
                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="currentColor" />
                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="currentColor" />
                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="currentColor" />
                                    </svg>
                                </button>

                                <button
                                    type="button"
                                    className="auth-social-btn-circle"
                                    onClick={() => handleSocialLogin('apple')}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                                    </svg>
                                </button>
                            </div>

                            <p className="auth-footer-link" style={{ marginTop: '1.5rem' }}>
                                {t.authHaveAccount} <button className="auth-link" onClick={() => setFormType('signIn')}>{t.authLogInLink}</button>
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="auth-subtitle">{t.authStartJourney}</p>
                            <p className="auth-description">
                                {t.authVerifyDescription(email)}
                            </p>
                            <form className="auth-form" onSubmit={handleVerify}>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder={t.authVerificationPlaceholder}
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value)}
                                    />
                                </div>
                                <button type="submit" className="auth-submit-btn">{t.authVerify}</button>
                            </form>
                        </>
                    )}
                </div>

            </div>
            {activePolicy && (
                <div className="policy-modal-overlay">
                    <div className="policy-modal">
                        <button className="policy-close-btn" onClick={() => setActivePolicy(null)} aria-label="Close policy">
                            ×
                        </button>
                        <div className="policy-header">
                            <div className="policy-star">✦</div>
                            <h2 className="policy-title">{policyContent[activePolicy].title}</h2>
                        </div>
                        <p className="policy-subtitle">{policyContent[activePolicy].subtitle}</p>
                        <div className="policy-scroll">
                            {policyContent[activePolicy].sections.map((section, index) => (
                                <div className="policy-section" key={section.heading}>
                                    <h3>{section.heading}</h3>
                                    <ul>
                                        {section.items.map((item, idx) => (
                                            <li key={`${section.heading}-${idx}`}>{item}</li>
                                        ))}
                                    </ul>
                                    {index !== policyContent[activePolicy].sections.length - 1 && <div className="policy-divider">✦</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AuthPage
